import React from 'react';
import { Activity, Pause, Power, Radio, Cpu, Zap, WifiOff } from 'lucide-react';
import { isStale, ageLabel } from '../../utils/staleness';

// Active when any single channel is drawing more than this many amps.
// CT noise floor is ~0.1A; below 0.5A across all channels we call it idle.
const ACTIVE_CHANNEL_AMPS = 0.5;
const ACTIVE_TOTAL_AMPS = 1.5;

// Extract { channel, amps } pairs from a latest{} block. Single source of
// schema truth — when the channel key format changes, only this changes.
const readChannels = (latest) => {
  if (!latest) return [];
  return Object.entries(latest)
    .filter(([k]) => /^ch\d{2}_amps$/.test(k))
    .map(([k, amps]) => ({ channel: parseInt(k.slice(2, 4), 10), amps: Number(amps) || 0 }))
    .sort((a, b) => a.channel - b.channel);
};

const MachineTile = ({ machine, data, onClick }) => {
  const d = data || machine || {};
  const monitorKind = d.monitorKind || machine.monitorKind || 'unknown';
  const stale = isStale(d.timestamp);

  // Status derivation — for analog, ACTIVE if any channel above threshold.
  let execution = 'OFFLINE';
  if (!stale) {
    if (monitorKind === 'analog') {
      const channels = readChannels(d.latest);
      const total = Number(d.latest?.totalAmps) || 0;
      const topAmps = channels.length > 0 ? Math.max(...channels.map((c) => c.amps)) : 0;
      execution = topAmps >= ACTIVE_CHANNEL_AMPS || total >= ACTIVE_TOTAL_AMPS ? 'ACTIVE' : 'IDLE';
    } else {
      execution = 'UNKNOWN';
    }
  }

  const statusConfig = {
    ACTIVE:  { dot: 'status-dot-active',  border: 'glow-border-active',  badge: 'badge-active',  label: 'RUNNING', icon: Activity },
    IDLE:    { dot: 'status-dot-idle',    border: 'glow-border-idle',    badge: 'badge-idle',    label: 'IDLE',    icon: Pause },
    UNKNOWN: { dot: 'status-dot-offline', border: '',                    badge: 'badge bg-slate-500/15 text-slate-400 border-slate-500/20', label: 'NO SCHEMA', icon: Power },
    OFFLINE: { dot: 'status-dot-offline', border: '',                    badge: 'badge bg-slate-500/15 text-slate-400 border-slate-500/20', label: 'OFFLINE', icon: WifiOff },
  };
  const status = statusConfig[execution];
  const StatusIcon = status.icon;

  const getMonitorBadge = () => {
    switch (monitorKind) {
      case 'analog':  return <span className="badge-bsr"><Radio className="w-3 h-3" />Analog</span>;
      case 'digital': return <span className="badge-digital"><Zap className="w-3 h-3" />Digital</span>;
      case 'cnc':     return <span className="badge-focas"><Cpu className="w-3 h-3" />CNC</span>;
      default:        return null;
    }
  };

  // ── Stale placeholder — shown when no recent push ────────
  const renderStalePlaceholder = () => (
    <div className="py-2 text-center">
      <p className="text-[10px] uppercase tracking-wider text-slate-600">No live data</p>
      <p className="text-[10px] text-slate-600 mt-1">Last seen: {ageLabel(d.timestamp)}</p>
    </div>
  );

  // ── Analog Tile — 14 channels of current ──────────────────
  const renderAnalogTile = () => {
    if (stale) return renderStalePlaceholder();
    const channels = readChannels(d.latest);
    const total = Number(d.latest?.totalAmps ?? 0);
    if (channels.length === 0) {
      return <p className="text-[10px] text-slate-600 text-center py-2">No channel data</p>;
    }

    const top = [...channels].sort((a, b) => b.amps - a.amps).slice(0, 3);

    return (
      <>
        {/* Total current */}
        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Total Current</p>
          <div className="flex items-baseline gap-2">
            <p className="metric-md text-slate-100">
              {total.toFixed(2)}<span className="text-slate-500 font-normal text-xs"> A</span>
            </p>
            <p className="text-[10px] text-slate-600">{channels.length} channels</p>
          </div>
        </div>

        {/* Top channels by draw */}
        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Top channels</p>
          <div className="space-y-1">
            {top.map((c) => (
              <div key={c.channel} className="flex items-center gap-2 text-xs">
                <span className="font-mono text-slate-500 w-8">CH{String(c.channel).padStart(2, '0')}</span>
                <div className="flex-1 h-1.5 rounded-full bg-surface-700 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${c.amps >= ACTIVE_CHANNEL_AMPS ? 'bg-emerald-500' : 'bg-slate-600'}`}
                    style={{ width: `${Math.min(100, (c.amps / Math.max(total, 0.001)) * 100)}%` }}
                  />
                </div>
                <span className="font-mono text-slate-300 tabular-nums w-12 text-right">{c.amps.toFixed(2)} A</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="pt-2.5 border-t border-surface-600/20">
          <p className="text-[10px] text-slate-600">
            Current only &bull; {ageLabel(d.timestamp)}
          </p>
        </div>
      </>
    );
  };

  // ── Digital / CNC: not yet wired to v2 schema ──────────────
  const renderPlaceholderTile = (label) => (
    <div className="py-3 text-center">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="text-[10px] text-slate-600 mt-1">Firmware not yet on v2 schema</p>
    </div>
  );

  const renderContent = () => {
    switch (monitorKind) {
      case 'analog':  return renderAnalogTile();
      case 'digital': return renderPlaceholderTile('Digital monitoring');
      case 'cnc':     return renderPlaceholderTile('CNC monitoring');
      default:        return renderPlaceholderTile('Unknown monitor type');
    }
  };

  return (
    <div
      className={`card-hover p-0 cursor-pointer overflow-hidden ${status.border}`}
      onClick={() => onClick(machine)}
    >
      <div className={`h-0.5 w-full ${
        execution === 'ACTIVE' ? 'bg-emerald-500' :
        execution === 'IDLE' ? 'bg-blue-500' :
        'bg-slate-700'
      }`} />

      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={status.dot} />
            <span className={status.badge}>
              <StatusIcon className="w-3 h-3" />
              {status.label}
            </span>
          </div>
          {getMonitorBadge()}
        </div>

        {/* Name */}
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-slate-100 leading-tight">{d.name || machine.name || machine.id}</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {d.location || machine.location || <span className="text-slate-600">No location set</span>}
          </p>
        </div>

        {/* Type-specific body */}
        {renderContent()}
      </div>
    </div>
  );
};

export default MachineTile;
